import React, { useRef, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "../index.css";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import GOQiiButton from "../Components/GoqiiButton";
import { navigateWithFai, triggerShare } from "../utils/Utils";
import { useTranslation } from "react-i18next";

// Shimmer helpers (same pattern as other lifestyle cards)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const WeightChartSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        position: "relative",
      }}
    >
      {/* SectionHeader skeleton */}
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />

          {/* Title + subtitle */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                width: "60%",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                width: "40%",
                borderRadius: 8,
              }}
            />
          </div>

          {/* Info icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 20,
              height: 20,
              borderRadius: 999,
            }}
          />
        </div>
      </div>

      {/* Chart skeleton */}
      <div
        style={{
          marginTop: 12,
          padding: "0 16px 80px 16px",
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 220,
            borderRadius: 16,
          }}
        />
      </div>

      {/* Bottom button skeleton */}
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 40,
            width: 200,
            borderRadius: 999,
          }}
        />
      </div>
    </div>
  );
};

const WeightChart = ({ shareConfig } = {}) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);


  useEffect(() => {
    const { encryptedId } = getUrlParams();

    if (!encryptedId) return;

    lifestyleApi
      .weightData(encryptedId)
      .then((response) => {
        if (response?.data?.code === 200) {
          setChartData(response.data.data);
        } else {
          console.error("Invalid response:", response);
        }
      })
      .catch((err) => console.error("Error loading weight data:", err));
  }, []);



  if (!chartData) return <WeightChartSkeleton />;


  const options = {
    chart: {
      type: "spline",
      backgroundColor: "transparent",
      // height: "300px",
      spacingTop: 10,
      spacingBottom: 0,
      marginBottom: 70,
      height: 150,
      // spacingLeft: 45,
      // spacingRight: 45,
      style: {
        fontFamily: "'Open Sans', sans-serif",
      },
      events: {
        render() {
          const chart = this;
          const isMobile = chart.chartWidth < 768;
          const isSmallMobile = chart.chartWidth < 480;

          if (chart.customElements) chart.customElements.forEach((el) => el.destroy());
          chart.customElements = [];

          const subTextFontSize = "9px";
          const buttonWidth = isSmallMobile ? 120 : 140;
          const buttonHeight = isSmallMobile ? 35 : 40;
          const buttonX = chart.chartWidth / 2 - buttonWidth / 2;
          const buttonY = chart.chartHeight - 45 - buttonHeight + 55;

          const subTextY = buttonY + 9;

          const arrowParts = chartData.arrow ? chartData.arrow.split(' ') : [];
          const arrowType = arrowParts[0] || "fa-caret-right";
          const arrowColorFromData = arrowParts[1] || chartData.arrowColor || "#666";

          const arrowSymbol = arrowType === "fa-caret-up" ? "▲" :
            arrowType === "fa-caret-down" ? "▼" : "";

          const arrowColor = arrowColorFromData === "green" ? "#008000" :
            arrowColorFromData === "red" ? "#ff0000" :
              arrowColorFromData === "orange" ? "#ff6600" :
                arrowColorFromData;

          const numberTextContent = `${arrowSymbol} ${chartData.subText2}`;
          const sinceTextContent = chartData.subText3;

          const totalEstimatedWidth = (numberTextContent.length + sinceTextContent.length) * (isSmallMobile ? 6 : 7);
          const startX = chart.chartWidth / 2 - totalEstimatedWidth / 2;

          const numberText = chart.renderer
            .text(numberTextContent, startX, subTextY)
            .css({
              fontSize: subTextFontSize,
              fontWeight: "bold",
              color: arrowColor,
              fontFamily: "Open Sans, sans-serif",
            })
            .add();
          chart.customElements.push(numberText);

          const numberTextBBox = numberText.getBBox();

          const sinceText = chart.renderer
            .text(sinceTextContent, startX + numberTextBBox.width + 10, subTextY)
            .css({
              fontSize: subTextFontSize,
              fontWeight: "bold",
              color: "var(--text-secondary)",
              fontFamily: "Open Sans, sans-serif",
            })
            .add();
          chart.customElements.push(sinceText);

        },
      },
    },
    title: { text: null },
    xAxis: {
      categories: chartData.xAxis,
      lineColor: "var(--border-subtle)",
      lineWidth: 1,
      labels: {
        style: {
          fontSize: "9px",
          color: "var(--text-primary)",
          fontFamily: "Open Sans, sans-serif"
        }
      },
    },
    yAxis: {
      // min: 0,
      // max: 110,
      lineColor: "var(--border-subtle)",
      lineWidth: 0,
      gridLineColor: "var(--border-subtle)",
      gridLineWidth: 0,
      // tickInterval: 20,
      title: { text: null },
      labels: {
        enabled: false,
        style: {
          fontSize: "12px",
          color: "var(--text-primary)",
          fontFamily: "Open Sans, sans-serif"
        },
        formatter: function () {
          return `${this.value}`;
        }
      },
    },
    legend: { enabled: false },
    tooltip: {
      enabled: false,
      formatter: function () {
        return `<b>${this.x}</b><br/>Weight: <b>${this.y}</b>`;
      }
    },
    plotOptions: {
      spline: {
        lineWidth: 4,
        color: chartData.color,
        marker: {
          enabled: false,
          radius: 5,
          lineWidth: 2,
          fillColor: "var(--bg-surface)",
          lineColor: "#4286f5",
          states: {
            hover: {
              enabled: false,
              radius: 7
            }
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "12px",
            fontWeight: "bold",
            color: "var(--text-primary)",
            textOutline: "none",
            fontFamily: "Open Sans, sans-serif"
          },
          align: 'center',
          verticalAlign: 'top',
          y: -30,
          formatter: function () {
            return `${this.y}`;
          }
        }
      }
    },
    series: [
      {
        name: t("weightChart.seriesName"),
        data: chartData.yAxis.map((p, index) => {
          const yValue = typeof p === 'object' ? p.y : p;
          return {
            y: yValue,
            color: "#4286f5",
            marker: {
              lineColor: "#4286f5",
              fillColor: "var(--bg-surface)",
            }
          };
        }),
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
        paddingBottom: 0,
      }}
    >
      <SectionHeader
        icon="https://appcdn.goqii.com/storeimg/80755_1764052267.png"
        title={chartData.title}
        subtitle={chartData.heading}
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        showInfo
        onInfoClick={() => {
          navigateWithFai("Weight History", chartData.infoUrl);
        }}
        showShare={!!shareConfig?.isSharable}
        onShareClick={() => triggerShare(shareConfig?.text)}
      />

      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />

      {/* GOQiiButton placed in the same position as the original custom button */}
      <div style={{
        // position: "absolute",
        paddingBottom: "10px",
        // paddingTop: "0px",
        // left: "50%",
        // transform: "translateX(-50%)",
        width: "100%",
        display: "flex",
        justifyContent: "center"
      }}>
        <GOQiiButton
          text={chartData.buttonTitle}
          color="green"
          solid={true}
          shape="rounded_edges"
          font="bold"
          onClick={() => window.open(chartData.url, "_blank")}
          
        />
      </div>
    </div>
  );
};

export default WeightChart;